import { Link, useParams } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { ROUTES } from '@/app/routes';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { LabMediaThumb } from '../components/LabMediaThumb';
import {
  pendingScoreBarColor,
  pendingScoreChipClass,
  scoreBarColor,
  scoreChipClass,
  type HistoryRiskIndicator,
} from '../lib/combinedRiskDisplay';
import {
  labDeepVerdict,
  labRiskIndicators,
  verdictBadgeClass,
} from '../lib/labResultDisplay';
import { useLabCase, useLabRun, type LabRunResult } from '../labApi';

function sourceFromResult(result: LabRunResult | undefined): string {
  const gpu = result?.resultJson?.gpu_moderation as Record<string, unknown> | undefined;
  const source = gpu?.source;
  return typeof source === 'string' ? source : '—';
}

function RiskChip({ indicator }: { indicator: HistoryRiskIndicator }) {
  const pending = false;
  const pct = Math.min(Math.max(indicator.score, 0), 1) * 100;
  const barClass = pending ? pendingScoreBarColor() : scoreBarColor(indicator.score);
  const chipClass = pending ? pendingScoreChipClass() : scoreChipClass(indicator.score);
  if (indicator.kind === 'meta') {
    return (
      <Badge
        variant="outline"
        className="rounded-full px-2 py-0 text-[10px] font-medium"
        title={indicator.title}
      >
        {indicator.label}
      </Badge>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5" title={indicator.title}>
      <span className="relative h-1.5 w-10 shrink-0 overflow-hidden rounded-full bg-muted" aria-hidden>
        <span
          className={cn('absolute inset-y-0 left-0 rounded-full', barClass)}
          style={{ width: `${pct}%` }}
        />
      </span>
      <Badge
        variant="outline"
        className={cn(
          'rounded-full px-2 py-0 text-[10px] font-medium tabular-nums',
          chipClass,
        )}
      >
        {indicator.label}
      </Badge>
    </span>
  );
}

function EngineCard({
  title,
  result,
}: {
  title: string;
  result: LabRunResult | undefined;
}) {
  const verdict = labDeepVerdict(result);
  const indicators = labRiskIndicators(result);
  const scores = indicators.filter((i) => i.kind !== 'meta');
  const models = indicators.filter((i) => i.kind === 'meta');

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-medium">{title}</h3>
        <span className="text-xs text-muted-foreground">
          {result?.status ?? 'missing'}
          {result?.durationMs != null ? ` · ${result.durationMs}ms` : ''}
        </span>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Badge
          variant="outline"
          className={cn('rounded-full px-2.5 py-0.5 text-xs font-semibold', verdictBadgeClass(verdict.label))}
          title={verdict.detail}
        >
          {verdict.label}
        </Badge>
        {verdict.risk != null ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            risk {verdict.risk.toFixed(3)}
          </span>
        ) : null}
        <span className="text-xs text-muted-foreground">· {sourceFromResult(result)}</span>
      </div>

      <p className="mb-3 text-xs text-muted-foreground">{verdict.detail}</p>

      {scores.length > 0 ? (
        <div className="space-y-2">
          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Policy confidence
          </div>
          <div className="flex flex-wrap gap-2">
            {scores.map((indicator) => (
              <RiskChip key={indicator.id} indicator={indicator} />
            ))}
          </div>
          {models.length > 0 ? (
            <div className="flex flex-wrap gap-1.5 pt-1">
              {models.map((indicator) => (
                <RiskChip key={indicator.id} indicator={indicator} />
              ))}
            </div>
          ) : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">
          No policy confidence chips in this package. Re-run after media-manager lab vectors
          deploy, or expand raw JSON below.
        </p>
      )}

      {result?.errorMessage ? (
        <p className="mt-3 text-xs text-destructive">{result.errorMessage}</p>
      ) : null}

      <details className="mt-4">
        <summary className="cursor-pointer text-xs text-muted-foreground">Raw JSON</summary>
        <pre className="mt-2 max-h-64 overflow-auto rounded bg-muted p-2 text-[11px] leading-snug">
          {JSON.stringify(result?.resultJson ?? null, null, 2)}
        </pre>
      </details>
    </div>
  );
}

export function PipelineLabRunPage() {
  const { caseId, runId } = useParams<{ caseId: string; runId: string }>();
  const { data: labCase } = useLabCase(caseId);
  const { data: run, isLoading } = useLabRun(runId);

  const fixturesById = new Map((labCase?.fixtures ?? []).map((f) => [f.id, f]));
  const fixtureIds = Array.from(
    new Set((run?.results ?? []).map((r) => r.fixtureId)),
  ).sort((a, b) => a - b);

  return (
    <div className="flex flex-col gap-6 p-6">
      <nav className="flex items-center gap-1 text-sm text-muted-foreground">
        <Link to={ROUTES.moderation} className="hover:text-foreground">
          Moderation
        </Link>
        <ChevronRight className="size-4" />
        <Link to={ROUTES.pipelineLab} className="hover:text-foreground">
          Pipeline Lab
        </Link>
        <ChevronRight className="size-4" />
        {caseId ? (
          <Link to={ROUTES.pipelineLabCase(caseId)} className="hover:text-foreground">
            {labCase?.name ?? caseId}
          </Link>
        ) : null}
        <ChevronRight className="size-4" />
        <span className="text-foreground">Run #{runId}</span>
      </nav>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : !run ? (
        <p className="text-sm text-destructive">Run not found</p>
      ) : (
        <>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Run #{run.id}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              {run.status}
              {run.startedAt ? ` · started ${new Date(run.startedAt).toLocaleString()}` : ''}
              {run.finishedAt
                ? ` · finished ${new Date(run.finishedAt).toLocaleString()}`
                : ''}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Verdict uses TipMi Pass-2 bands on deep risk_score (Approved &lt; 0.50, Frontier &lt;
              0.60, Human &lt; 0.70, else Rejected). Lab does not publish content.
            </p>
            {run.errorMessage ? (
              <p className="mt-2 text-sm text-destructive">{run.errorMessage}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-6">
            {fixtureIds.map((fixtureId) => {
              const fixture = fixturesById.get(fixtureId);
              const gpu = run.results?.find(
                (r) => r.fixtureId === fixtureId && r.engine === 'gpu',
              );
              const cloud = run.results?.find(
                (r) => r.fixtureId === fixtureId && r.engine === 'cloud',
              );
              return (
                <section key={fixtureId} className="rounded-lg border bg-card p-4">
                  <div className="mb-4 flex gap-3">
                    <LabMediaThumb
                      previewUrl={fixture?.previewUrl}
                      mediaFileType={fixture?.mediaFileType}
                      mediaContentType={fixture?.mediaContentType}
                    />
                    <div className="min-w-0">
                      <div className="text-sm font-medium">Fixture #{fixtureId}</div>
                      <div className="whitespace-pre-wrap text-sm text-muted-foreground">
                        {fixture?.caption?.trim() || '(no caption)'}
                      </div>
                    </div>
                  </div>
                  <div className="grid gap-4 lg:grid-cols-2">
                    <EngineCard title="GPU (Gemma)" result={gpu} />
                    <EngineCard title="Cloud (Bedrock deep)" result={cloud} />
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
